import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, MagnifyingGlassIcon } from '@itsrever/design-system'

const { Button, Card, Input } = Base
import { clsx } from 'clsx'
import { DashboardUser } from '@/domain/dashboard-user'
import {
  ContactType,
  EcommerceContact,
  pointOfContactFor
} from '@/domain/ecommerce-contact'
import { SaveUserInput, UserDialog } from './user-dialog'
import { WarehouseOption, roleLabelKey, warehouseLabel } from './user-roles'
import { contactTypeLabelKey } from './contact-types'
import { PageHeader } from '@/ui/shared'

const ROW_LAYOUT =
  'grid grid-cols-[2fr_1fr_1.5fr_1.5fr_auto_200px] items-center gap-4 px-4'

interface UserManagementProps {
  users: DashboardUser[]
  // Contacts of the ecommerce on screen; a user is a point of contact through their email.
  contacts: EcommerceContact[]
  warehouses: WarehouseOption[]
  // Email of the logged-in admin: they cannot change their own access, so their row offers no
  // actions. The API refuses it as well; this only keeps the UI from offering a dead end.
  currentUserEmail?: string
  saving: boolean
  onSave: (input: SaveUserInput, options?: { onSuccess?: () => void }) => void
  onSetAccess: (input: { id: string; revoked: boolean }) => void
}

export const UserManagement: React.FC<UserManagementProps> = ({
  users,
  contacts,
  warehouses,
  currentUserEmail,
  saving,
  onSave,
  onSetAccess
}) => {
  const { t } = useTranslation()
  const [editedUser, setEditedUser] = useState<DashboardUser | undefined>()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [search, setSearch] = useState('')

  const query = search.trim().toLowerCase()
  const visibleUsers = query
    ? users.filter((user) => user.email.toLowerCase().includes(query))
    : users

  const openInvite = () => {
    setEditedUser(undefined)
    setDialogOpen(true)
  }

  const openEdit = (user: DashboardUser) => {
    setEditedUser(user)
    setDialogOpen(true)
  }

  // The dialog closes on success only: closing on submit would discard the typed email and
  // permissions on a failure, and unmount the spinner while the save is still in flight.
  const handleSave = (input: SaveUserInput) => {
    onSave(input, { onSuccess: () => setDialogOpen(false) })
  }

  return (
    <>
      <PageHeader
        title={t('dashboard_users.title')}
        description={t('dashboard_users.description')}
        actions={
          <Button type="button" variant="primary" onClick={openInvite}>
            {t('dashboard_users.invite')}
          </Button>
        }
      />
      <div className="flex max-w-screen-xl flex-col gap-4 px-7 py-4">
        {users.length > 0 ? (
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            type="search"
            placeholder={t('dashboard_users.search')}
            aria-label={t('dashboard_users.search')}
            prefix={<MagnifyingGlassIcon className="size-5" />}
            clear={search.length > 0}
            onClear={() => setSearch('')}
          />
        ) : null}

        <Card variant="flat">
          <div
            className={clsx(
              ROW_LAYOUT,
              'border-b border-neutral-200 py-2 text-xs text-grey-1'
            )}
          >
            <p>{t('dashboard_users.user')}</p>
            <p>{t('dashboard_users.role')}</p>
            <p>{t('dashboard_users.warehouses')}</p>
            <p>{t('dashboard_users.point_of_contact_column')}</p>
            <p>{t('dashboard_users.status')}</p>
            <p className="sr-only">{t('dashboard_users.actions')}</p>
          </div>

          {visibleUsers.length === 0 ? (
            <p className="px-4 py-6 text-sm text-neutral-600">
              {query
                ? t('dashboard_users.no_matches')
                : t('dashboard_users.empty')}
            </p>
          ) : null}

          {visibleUsers.map((user) => (
            <div
              key={user.id}
              className={clsx(
                ROW_LAYOUT,
                'border-b border-neutral-100 py-3 last:border-b-0'
              )}
            >
              <p className="truncate text-sm">{user.email}</p>
              <p className="truncate text-sm">{t(roleLabelKey(user.role))}</p>
              <p className="truncate text-sm text-neutral-600">
                {warehouseNames(warehouses, user.assignedWarehouses)}
              </p>
              <p className="truncate text-sm text-neutral-600">
                {contactTypeNames(
                  pointOfContactFor(contacts, user.email).types,
                  t
                )}
              </p>
              <span
                className={clsx(
                  'rounded-full px-2 py-0.5 text-center text-xs',
                  user.accessRevoked
                    ? 'bg-red-50 text-error-main'
                    : 'bg-green-50 text-green-700'
                )}
              >
                {user.accessRevoked
                  ? t('dashboard_users.revoked')
                  : t('dashboard_users.active')}
              </span>
              <div className="flex justify-end gap-2">
                {isCurrentUser(user, currentUserEmail) ? (
                  <p className="text-xs text-neutral-500">
                    {t('dashboard_users.you')}
                  </p>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => openEdit(user)}
                    >
                      {t('dashboard_users.edit')}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        onSetAccess({
                          id: user.id,
                          revoked: !user.accessRevoked
                        })
                      }
                    >
                      {user.accessRevoked
                        ? t('dashboard_users.restore')
                        : t('dashboard_users.revoke')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </Card>

        {dialogOpen ? (
          <UserDialog
            isOpen={dialogOpen}
            user={editedUser}
            pointOfContact={
              editedUser
                ? pointOfContactFor(contacts, editedUser.email)
                : undefined
            }
            contacts={contacts}
            warehouses={warehouses}
            saving={saving}
            onClose={() => setDialogOpen(false)}
            onSave={handleSave}
          />
        ) : null}
      </div>
    </>
  )
}

function isCurrentUser(user: DashboardUser, currentUserEmail?: string) {
  if (!currentUserEmail) return false
  return user.email.toLowerCase() === currentUserEmail.toLowerCase()
}

function contactTypeNames(types: ContactType[], t: (key: string) => string) {
  if (types.length === 0) return '—'
  return types.map((type) => t(contactTypeLabelKey(type))).join(', ')
}

function warehouseNames(warehouses: WarehouseOption[], assigned: string[]) {
  if (assigned.length === 0) return '—'
  return assigned.map((id) => warehouseLabel(warehouses, id)).join(', ')
}
